import React from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { getCountryByCode } from 'country-state-city/src/country';
import { useSelector } from 'react-redux';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import { Building } from './AddBuildingModal';
import { UserPermission } from '../../../constants/Types';
import { Area, Floor, Space } from '../../../components/Map/SetFloorsModal/FloorItem';
import PressableStyled from '../../../components/PressableStyled';
import titleCase from '../../../components/TitleCase';
import SettingsIcon from '../../../components/24pxIcons/SettingsIcon';

type Section = {
  title: string,
  count: string | number,
}

const SectionItem = ({ title, count }: Section) => <View>
  <Text style={styles.sectionTitle}>{title}</Text>
  <Text style={styles.sectionCount}>{count}</Text>
</View>;

interface BuildingTitleItemProps {
  selectedBuilding: Building,
  showBuildingSettings: () => void,
}

const BuildingTitleItem = ({
  selectedBuilding, showBuildingSettings,
}: BuildingTitleItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);

  const floors: Floor[] = Object.values(selectedBuilding?.floors ?? {});
  let assetCount: number = 0;
  let sensorCount: number = 0;
  let meterCount: number = 0;
  let areaCount: number = 0;
  let spaceCount: number = 0;
  floors.forEach((floor: Floor | null) => {
    if (!floor) return;
    assetCount += floor.assetCount ?? 0;
    sensorCount += floor.sensorCount ?? 0;
    meterCount += floor.meterCount ?? 0;
    Object.values(floor.areas ?? {}).forEach((area: Area) => {
      if (!area) return;
      areaCount += 1;
      assetCount += area.assetCount ?? 0;
      sensorCount += area.sensorCount ?? 0;
      meterCount += area.meterCount ?? 0;
      Object.values(area.spaces ?? {}).forEach((space: Space) => {
        if (!space) return;
        spaceCount += 1;
        assetCount += space.assetCount ?? 0;
        sensorCount += space.sensorCount ?? 0;
        meterCount += space.meterCount ?? 0;
      });
    });
  });

  return <View style={[Style.row, styles.container]}>
    <View style={[Style.row, styles.titleSection]}>
      <View>
        <Text style={styles.title}>{titleCase(selectedBuilding.Name)}</Text>
        <Text style={styles.location}>
          {selectedBuilding.CityName ? titleCase(selectedBuilding.CityName) : ''}
          {(selectedBuilding.CityName && getCountryByCode(selectedBuilding.CountryCode))
            ? ', ' : ''}
          {getCountryByCode(selectedBuilding.CountryCode)?.name ?? ''}
        </Text>
      </View>
      {canUpdateLocations && <PressableStyled
        style={styles.icon}
        onPress={showBuildingSettings}
        testID='lm-building-settings-btn'
      >
        <SettingsIcon />
      </PressableStyled>}
    </View>
    <View style={[Style.row, { flex: 1 }]}>
      <SectionItem
        title='FLOORS'
        count={floors.length}
      />
      <SectionItem
        title='AREAS'
        count={areaCount}
      />
      <SectionItem
        title='SPACES'
        count={spaceCount}
      />
      <SectionItem
        title='ASSETS'
        count={assetCount}
      />
      {happyEnabled && <SectionItem
        title='SENSORS & METERS'
        count={sensorCount + meterCount}
      />}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginBottom: 40,
    marginHorizontal: 24,
    flexWrap: 'wrap',
  },
  titleSection: {
    minWidth: 300,
    padding: 12,
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
    backgroundColor: Colors.light.hereworksWhite100,
    marginEnd: 12,
  },
  title: {
    fontSize: 18,
    lineHeight: 28,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  location: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  icon: {
    marginStart: 24,
  },
  sectionTitle: {
    fontSize: 11,
    lineHeight: 13,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  sectionCount: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
});

export default BuildingTitleItem;
